import { BindAll } from 'lodash-decorators'
import { action, computed, observable, toJS } from 'mobx'
import { reportEntryLog } from '@xt/client/utils/log'
import { $service } from '@xt/client/experimental/request/service'
import moment from 'moment'
import { EnumApiMy, EnumApiUser, EnumApiProduct, EnumApiHomework } from '../../api'
import type { CourseCardUIType } from './types.d'
import type { CourseItem, MyCourseList, ICalendarData } from '@xt/client/types/my-go-course'
import type {
  MyClassroomType,
  StudyReviewType,
  UserStatisticsResponse,
  CalendarTimeData,
  StudyRecordResp,
  UserCenterResponse,
  MyLastReviewResponse,
  StudentCardData,
  MyAccountCountType,
  FocusCountResponse
} from '@xt/client/entities/my-v2/types'
import {
  transfromProductList,
  formatYearMonthDay,
  formatMomentCountUnit,
  formatCountTenThousand,
  transformLearnTimeNumber
} from '@xt/client/utils/my'
import { AjaxBasics } from '@xt/client/helpers/ajaxBasics'
import lodash, { isNumber } from 'lodash'
import { EnumMyAccountCount, EnumMyStudyMenu } from '@xt/client/enums/my'

type MyProductStatus = {
  loading: boolean
  isError: boolean
  haveNoneCourse: boolean
}

@BindAll()
export class MyClassroomController {
  @observable userPanelData: MyClassroomType['UserPanel'] = {
    follow: 0,
    followed: 0,
    like: 0
  }
  @observable studyRecordData: MyClassroomType['StudyRecord'] | null = null
  @observable myProductList: Array<CourseItem & CourseCardUIType> = []
  @observable myProductStatus: MyProductStatus = {
    loading: true,
    haveNoneCourse: false,
    isError: false
  }
  @observable lastReview: StudyReviewType | null = null
  @observable studyActionData: MyClassroomType['StudyAction'] = {
    homework: 0,
    reward: 0,
    daily: 0,
    collect: 0
  }

  @observable calendarTimeData: Array<CalendarTimeData> = []
  @observable allClasshourList: Array<ICalendarData> = []

  // 缓存的月份日历数据，学习记录，课时列表
  @observable cacheCalendarTime: Record<string, Array<CalendarTimeData>> = {}
  @observable cacheStudyRecord: Record<string, Array<StudyRecordResp>> = {}
  @observable cacheClasshourList: Record<string, Array<ICalendarData>> = {}

  @observable studentCardData: StudentCardData | null = null
  @observable myAccountCount: MyAccountCountType | null = null

  @computed
  get transformUserPanelData() {
    return {
      follow: formatCountTenThousand(this.userPanelData.follow),
      followed: formatCountTenThousand(this.userPanelData.followed),
      like: formatCountTenThousand(this.userPanelData.like)
    }
  }

  @computed
  get myAccountFormatData() {
    if (!this.myAccountCount) return

    return {
      [EnumMyAccountCount.Order]: this.canShow(this.myAccountCount.unPayOrderCounts)
        ? `${this.myAccountCount.unPayOrderCounts}笔待支付`
        : this.canShow(this.myAccountCount.unGroupOrderCounts)
          ? `${this.myAccountCount.unGroupOrderCounts}笔待拼团`
          : 0,
      [EnumMyAccountCount.Bishan]: this.canShow(this.myAccountCount.bishanpoints) ? `${this.myAccountCount.bishanpoints}个` : 0,
      [EnumMyAccountCount.Coin]: this.canShow(this.myAccountCount.coins) ? `${this.myAccountCount.coins}个` : 0,
      [EnumMyAccountCount.Packs]: this.canShow(this.myAccountCount.coupons) ? `${this.myAccountCount.coupons}张可用` : 0
    }
  }

  @computed
  get formatActionData() {
    return Object.entries(toJS(this.studyActionData)).map(([key, value]) => {
      const path = this.mapMyStudyPath(key)

      return {
        ...formatMomentCountUnit(value),
        urlSuffix: key,
        path
      }
    })
  }

  @computed
  get dataList() {
    return lodash.chunk(this.calendarTimeData, 7)
  }

  mapMyStudyPath(key: string) {
    let path = '/my/study'

    switch (key) {
      case 'homework':
        return path + `?menu=${EnumMyStudyMenu.Homework}`
      case 'reward':
        return path + `?menu=${EnumMyStudyMenu.Reward}`
      case 'daily':
        return path + `?menu=${EnumMyStudyMenu.Daily}`
      case 'collect':
        return path + `?menu=${EnumMyStudyMenu.Collect}`
      default:
        return path
    }
  }

  canShow(count: number | null) {
    return isNumber(count) && count !== 0
  }

  singlePadding(num) {
    return num <= 9 ? '0' + num : num
  }

  // 获取并缓存日历数据，课时数据，学习记录数据
  async loadCacheClassHourList(startTime: number, key: string) {
    if (this.cacheClasshourList[key]) return

    const list = await this.loadClasshourList(31, startTime)

    this.setCacheClassHourList(list, key)
  }
  async loadCacheStudyRecord(startTime: number, key: string) {
    if (this.cacheStudyRecord[key]) return

    const list = await this.loadStudyRecordList(startTime)

    this.cacheStudyRecord[key] = list
  }
  loadCacheCalendarTimeData(data: Array<CalendarTimeData>, key: string) {
    if (this.cacheCalendarTime[key]) return

    const firstDayWeek = data[0].w === 0 ? 7 : data[0].w
    const tempArr = Array.from({ length: firstDayWeek - 1 }).fill(null) as CalendarTimeData[]

    // 前面的空数组主要是为了占位使用
    this.setCacheCalendarTime([...tempArr, ...data], key)
  }
  // 获取指定时间学习记录
  async loadStudyRecordList(startTime: number) {
    const [err, data] = await $service<Array<StudyRecordResp>>({
      url: EnumApiProduct.productCalendarStudyRecord,
      method: 'post',
      data: {
        startTime,
        countDay: 31
      }
    })

    if (err) return []

    return data.map(item => {
      const { y, m, d } = formatYearMonthDay(item.studyTime)
      return { ...item, year: y, month: m, day: d }
    })
  }
  // 获取指定时间课程表（课时数据）
  async loadClasshourList(count: 21 | 31, startTime: number) {
    const [, res] = await $service<Array<ICalendarData>>({
      url: EnumApiProduct.productCalendar,
      method: 'post',
      data: {
        startTime,
        countDay: count
      }
    })
    // 数据处理
    let list = (res ?? []).map(i => {
      const { y, m, d } = formatYearMonthDay(i.startTime)
      return {
        ...i,
        year: y,
        month: m,
        day: d
      }
    })

    return list
  }
  // 获取上个月42天的时间数据
  getPrevMonthTimeData(date: Omit<CalendarTimeData, 'timestamp' | 'w'>) {
    const y = date.m === 1 ? date.y - 1 : date.y
    const m = date.m === 1 ? 12 : date.m - 1
    const prevMonthTime = moment(`${this.singlePadding(y)}-${this.singlePadding(m)}-01`).valueOf()
    return {
      data: this.get31DayCalendarData(prevMonthTime),
      cacheKey: `${y}-${m}`
    }
  }
  // 获取下个月42天的时间数据
  getNextMonthTimeData(date: Omit<CalendarTimeData, 'timestamp' | 'w'>) {
    const y = date.m === 12 ? date.y + 1 : date.y
    const m = date.m === 12 ? 1 : date.m + 1
    const nextMonthTime = moment(`${this.singlePadding(y)}-${this.singlePadding(m)}-01`).valueOf()
    return {
      data: this.get31DayCalendarData(nextMonthTime),
      cacheKey: `${y}-${m}`
    }
  }

  get31DayCalendarData(time?: number) {
    time = time ?? AjaxBasics.serviceDate.valueOf()
    const currentMonthStartMoment = moment(time).startOf('month')
    const currentMonthEndMoment = moment(time).endOf('month')
    // 加1是因为需要包含最后一天
    const currentMonthTotalDay = currentMonthEndMoment.diff(currentMonthStartMoment, 'day') + 1
    let startTime = currentMonthStartMoment.valueOf()
    const wholeTime = 24 * 60 * 60 * 1000

    const result: Array<CalendarTimeData> = []
    for (let i = 1; i <= currentMonthTotalDay; i++) {
      const time = moment(startTime)
      const [year, month, day] = time
        .format('YYYY-MM-DD')
        .split('-')
        .map(v => parseInt(v))

      result.push({ y: year, m: month, d: day, w: time.day(), timestamp: time.valueOf() })
      startTime += wholeTime
    }

    return result
  }

  get21DayCalendarData() {
    let week = AjaxBasics.serviceDate.utcOffset(+8).startOf('isoWeek')
    let dayTime = 24 * 1000 * 60 * 60
    let time = week.subtract(7, 'days').valueOf()
    let currWeek: any[] = []
    for (let i = 0; i < 21; i++) {
      let item = moment(time)
      const [y, m, d] = item
        .format('YYYY-MM-DD')
        .split('-')
        .map(v => parseInt(v))
      currWeek.push({ w: item.day(), y, m, d, timestamp: item.valueOf() })
      time += dayTime
    }
    this.setCalendarTimeData(currWeek)
  }

  // 初始化日历模块数据
  async initCalendarData() {
    // 获取21天日历数据
    this.get21DayCalendarData()
    // 初始化课时数据
    const list = await this.loadClasshourList(21, this.calendarTimeData[0].timestamp)
    this.setAllClasshourList(list)
  }

  // 获取学习互动数据
  async loadMyStudyData() {
    try {
      const [err, res] = await $service<MyLastReviewResponse>({
        url: EnumApiHomework.NewReviewed
      })

      if (err) throw err

      // 后台返回 id 为null 时，表示没有数据
      if (!res.id) return

      this.setLastReview({
        id: res.id,
        reviewed: res.reviewed,
        reviewerId: res.reviewerId,
        reviewerNickname: res.reviewerNickname,
        content: res.content,
        tag: res.title,
        cover: Array.isArray(res.picList) && res.picList.length ? res.picList[0].waterUrl : undefined,
        createTime: res.createTime
      })
    } catch (error) {
      reportEntryLog('classroom.loading-last-review-data.fail', { error })
    }
  }

  // 获取用户面板数据 及 （作业，作品，日记，收藏数量统计）
  async fetchUserPanelData(memberId: string) {
    const [err, res] = await $service<UserCenterResponse>({
      url: EnumApiHomework.UserCenter
    })

    if (err) return

    const { totalCollectCount, totalHomeworkCount, totalLikeCount, totalMomentCount, totalShowWorkCount } = res

    this.setStudyActionData({
      homework: totalHomeworkCount ?? 0,
      reward: totalShowWorkCount ?? 0,
      daily: totalMomentCount ?? 0,
      collect: totalCollectCount ?? 0
    })

    this.setUserPanelData({
      like: totalLikeCount ?? 0
    })

    const [err1, res1] = await $service<FocusCountResponse>({
      url: EnumApiUser.FocusCount,
      params: {
        memberId
      }
    })

    if (err1) return

    const { focusCount, fansCount } = res1

    this.setUserPanelData({
      follow: focusCount,
      followed: fansCount
    })
  }

  // 获取我的学习数据统计以及桐学证数据统计
  async fetchStudyRecordData(memberId: string) {
    try {
      const [err, res] = await $service<UserStatisticsResponse>({
        url: EnumApiUser.UserStatistics,
        data: {
          id: memberId
        }
      })

      if (err) throw err

      const {
        learnTimeCounts,
        learnTimeWeek,
        registeredTime,
        sequentialLearnDays,
        memberWeekRecordMap,
        userName,
        courseCounts,
        level,
        unGroupOrderCounts,
        unPayOrderCounts,
        bishanpoints,
        coins,
        coupons
      } = res

      let lastWeekLearnData = (memberWeekRecordMap ? Object.keys(memberWeekRecordMap) : []).sort().map(key => {
        return {
          date: moment(+key).valueOf(),
          time: +Math.floor(memberWeekRecordMap[key] / 60)
        }
      })

      this.setStudentCardData({
        userName: userName ?? '',
        joinDays: this.getDays(registeredTime),
        studyCourses: courseCounts ?? 0,
        level: level ?? 0
      })

      this.setMyAccountCount({
        unGroupOrderCounts: unGroupOrderCounts ?? 0,
        unPayOrderCounts: unPayOrderCounts ?? 0,
        bishanpoints: bishanpoints ?? 0,
        coins: coins ?? 0,
        coupons: coupons ?? 0
      })

      this.setStudyRecordData({
        learnTime: transformLearnTimeNumber(Math.round(((learnTimeCounts ?? 0) / 3600) * 10) / 10),
        weekLearnTime: Math.floor((learnTimeWeek ?? 0) / 60),
        continueDays: sequentialLearnDays ?? 0,
        lastWeekLearnData
      })
    } catch (error) {
      reportEntryLog('classroom.loading-study-record-data.fail', { error })
    }
  }

  getDays(registeredTime: number | null) {
    if (!registeredTime) return 0

    const now = AjaxBasics.serviceDate

    return Math.floor(moment.duration(now.diff(registeredTime)).asDays())
  }

  // 获取我的课程列表
  async loadMyProductList() {
    try {
      this.setMyProductStatus({
        loading: true
      })

      const [err, res] = await $service<MyCourseList>({
        url: EnumApiMy.MyCourseList
      })

      if (err) throw err

      this.setMyProductList([...transfromProductList(res)])

      this.setMyProductStatus({
        haveNoneCourse: this.myProductList.length === 0,
        isError: false
      })
    } catch (error) {
      reportEntryLog('toclass.loading-data.fail', { error })
      this.setMyProductStatus({
        isError: true
      })
    } finally {
      this.setMyProductStatus({
        loading: false
      })
    }
  }

  @action
  setMyAccountCount(data: MyAccountCountType) {
    this.myAccountCount = {
      ...data
    }
  }

  @action
  setStudentCardData(data: StudentCardData) {
    this.studentCardData = {
      ...data
    }
  }

  @action
  setCacheCalendarTime(list: Array<CalendarTimeData>, key: string) {
    this.cacheCalendarTime[key] = list
  }

  @action
  setCacheStudyRecord(list: Array<StudyRecordResp>, key: string) {
    this.cacheStudyRecord[key] = list
  }

  @action
  setCacheClassHourList(list: Array<ICalendarData>, key: string) {
    this.cacheClasshourList[key] = list
  }

  @action
  setAllClasshourList(list: Array<ICalendarData>) {
    this.allClasshourList = [...list]
  }

  @action
  setCalendarTimeData(list: Array<CalendarTimeData>) {
    this.calendarTimeData = [...list]
  }

  @action
  setStudyActionData(data: MyClassroomType['StudyAction']) {
    this.studyActionData = { ...data }
  }

  @action
  setLastReview(data: StudyReviewType) {
    this.lastReview = {
      ...data
    }
  }

  @action
  setMyProductList(res: Array<CourseItem & CourseCardUIType>) {
    this.myProductList = [...res]
  }

  @action
  setMyProductStatus(status: Partial<MyProductStatus>) {
    this.myProductStatus = {
      ...this.myProductStatus,
      ...status
    }
  }

  @action
  setUserPanelData(data: Partial<MyClassroomType['UserPanel']>) {
    this.userPanelData = {
      ...this.userPanelData,
      ...data
    }
  }

  @action
  setStudyRecordData(data: MyClassroomType['StudyRecord']) {
    this.studyRecordData = {
      ...data
    }
  }

  @action
  resetCalendarPopCache() {
    this.cacheCalendarTime = {}
    this.cacheStudyRecord = {}
    this.cacheClasshourList = {}
  }

  @action
  reset() {
    this.userPanelData = {
      follow: 0,
      followed: 0,
      like: 0
    }
    this.studyRecordData = null
    this.myProductList = []
    this.myProductStatus = {
      loading: true,
      haveNoneCourse: false,
      isError: false
    }
    this.lastReview = null
    this.studyActionData = {
      homework: 0,
      reward: 0,
      daily: 0,
      collect: 0
    }
    this.calendarTimeData = []
    this.allClasshourList = []

    this.cacheCalendarTime = {}
    this.cacheStudyRecord = {}
    this.cacheClasshourList = {}

    this.studentCardData = null
    this.myAccountCount = null
  }
}
